// Filename: src/screens/books/BookMainFile.jsx
// Current Date and Time (UTC): 2025-02-04 17:36:12
// Current User's Login: raghavkunkee

/**
 * BookMainFile Component
 * Primary component for displaying and managing book data.
 * Features:
 * - Responsive header with search, count, and refresh
 * - Data table with aligned columns and filter popups
 * - Search with dropdown suggestions
 * - Theme-consistent pagination
 * @author raghavkunkee
 */

import { useEffect, useRef, useState, useCallback, useContext } from 'react';
import moment from 'moment';
import { FilterMatchMode } from 'primereact/api';
import { Toast } from 'primereact/toast';
import { subscriberContext } from "../../App";
import numeral from 'numeral';
import styled from 'styled-components';

import noImage from '../../assets/images/noImage.jpg';
import Currency from '../../components/generic/currency';
import Loading from '../../components/generic/loading';
import { LoadingOverlay } from '../../components/generic/Styled';
import { ButtonRefresh } from "../../components/generic/button";
import { StyledLabelBlack } from '../../components/routing/Styled';
import BookTable from '../../containers/table/BookTable';
import TopFilter from './TopFilter';
import Pagination from './Pagination';
import ProductDetails from './ProductDetails';
import { fetchWithFallback, defaultBookData } from '../../utils/apiUtils';

// Styled Components
const HeaderContainer = styled.div`
    display: grid;
    grid-template-columns: 1fr 2fr 1fr;
    align-items: center;
    padding: 1rem;
    background: ${props => props.theme.colors.white};
    border: 1px solid ${props => props.theme.border.gray};
    border-radius: ${props => props.theme.radius.sizes.standard};
    margin-bottom: 1rem;
    box-shadow: ${props => props.theme.shadow.standard};

    @media (max-width: 768px) {
        grid-template-columns: 1fr;
        gap: 1rem;
    }
`;

const BookCountSection = styled.div`
    display: flex;
    align-items: center;
    gap: 0.5rem;
    
    .pi-book {
        font-size: 1.5rem;
        color: ${props => props.theme.colors.primary};
    }
    
    .book-count {
        color: ${props => props.theme.colors.font_dark};
        font-weight: ${props => props.theme.font.weights.semi_bold};
    }
`;

const SearchSection = styled.div`
    display: flex;
    justify-content: center;
`;

const RefreshSection = styled.div`
    display: flex;
    justify-content: flex-end;

    .refreshButton {
        background-color: #f7b0b0;
        color: #bc3232;
        border-radius: 50%;
        transition: all 0.2s;

        &:hover {
            background-color: #bc3232;
            color: #fff;
            transform: scale(1.1);
        }

        &:disabled {
            opacity: 0.6;
            transform: none;
        }
    }
`;

const TableContainer = styled.div`
    background: ${props => props.theme.colors.white};
    border-radius: ${props => props.theme.radius.sizes.standard};
    box-shadow: ${props => props.theme.shadow.standard};
    padding: 0;
    margin: 0 1rem;
    overflow: hidden;
    height: calc(100vh - 230px);
`;

const BookMainFile = ({
    setAllowBookMain,
    setProductDetail,
    setTableData,
    tableData,
    allowBookMain,
    productDetail,
    searchKey,
    setSearchKey
}) => {
    const { keycloak } = useContext(subscriberContext);
    const toast = useRef(null);
    
    // State management
    const [loading, setLoading] = useState(false);
    const [tableLoading, setTableLoading] = useState(false);
    const [refreshTrigger, setRefreshTrigger] = useState(false);
    const [page, setPage] = useState(1);
    const [size, setSize] = useState(20);
    const [totalNumberBooks, setTotalNumberBooks] = useState(0);
    const [lastPage, setLastPage] = useState(1);
    const [searchFil, setSearchFil] = useState(false);
    
    const [tableMainData, setTableMainData] = useState([]);
    const [filterTableMainData, setFilterTableMainData] = useState([]);
    const [searchFilter, setSearchFilter] = useState([]);
    const [filters, setFilters] = useState({});

    // Constants
    const sizeList = [
        { name: 20, value: 20 },
        { name: 30, value: 30 },
        { name: 40, value: 40 }
    ];

    // Data fetching with error handling
    const fetchBookData = useCallback(async () => {
        try {
            setTableLoading(true);
            const url = `${process.env.REACT_APP_APIPRODUCTURL}/v1/products?page=${page - 1}&size=${size}&sortby=status&sortOrder=ASC&status=ACTIVE`;
            const data = await fetchWithFallback(url, defaultBookData, {}, keycloak?.token);
            
            if (data?._embedded?.productVOList) {
                setTableMainData(data._embedded.productVOList);
                setLastPage(data.page?.totalPages || 1);
                setTotalNumberBooks(data.page?.totalElements || 0);
                
                // Show success message only if it was triggered by refresh button
                // if (tableLoading) {
                if (refreshTrigger) {   
                    toast.current.show({
                        severity: 'success',
                        summary: 'Success',
                        detail: 'Book data refreshed successfully',
                        life: 3000
                    });
                }
            }
        } catch (error) {
            console.error('Error fetching books:', error);
            toast.current.show({
                severity: 'error',
                summary: 'Error',
                detail: 'Failed to load book data',
                life: 3000
            });
        } finally {
            setTableLoading(false);
        }
    }, [page, size, keycloak?.token, refreshTrigger]);

    // Handlers
    const handleRefresh = useCallback(() => {
        setTableLoading(true); // Start loading immediately
        setRefreshTrigger(prev => !prev);
        toast.current.show({
            severity: 'info',
            summary: 'Refreshing',
            detail: 'Updating book data...',
            life: 3000
        });
    }, []);

    // Effects
    useEffect(() => {
        fetchBookData();
    }, [fetchBookData]);

    // Column definitions with memoization
    const defaultColumns = [
        // Improved implementation with proper image precedence
        {
            id: 'image',
            header: 'Image',
            body: (rowData) => {
                const getImageUrl = (imageData) => {
                    if (!imageData?.images || !imageData.images.length) {
                        return noImage;
                    }

                    // Find first image that has any of the types
                    const image = imageData.images[0];
                    
                    // Check in order of precedence
                    if (image?.thumbnail?.url && image.thumbnail.url !== 'NA') {
                        return image.thumbnail.url;
                    }
                    
                    if (image?.normal?.url && image.normal.url !== 'NA') {
                        return image.normal.url;
                    }
                    
                    if (image?.hires?.url && image.hires.url !== 'NA') {
                        return image.hires.url;
                    }

                    return noImage;
                };

                return (
                    <div
                        onClick={() => {
                            setAllowBookMain(false);
                            setProductDetail(rowData);
                        }}
                        className='flex align-items-center gap-2'
                        style={{ cursor: 'pointer' }}
                    >
                        <img
                            src={getImageUrl(rowData)}
                            alt={rowData?.title || 'Book cover'}
                            height='75px'
                            width='56px'
                            onError={(e) => {
                                e.target.src = noImage;
                            }}
                            style={{
                                objectFit: 'contain',
                                backgroundColor: '#fff',
                                border: '1px solid #ddd',
                                borderRadius: '4px'
                            }}
                        />
                    </div>
                );
            },
            width: '100px'
        },
        {
            id: 'primaryId',
            field: 'primaryId.id',
            header: 'ISBN',
            width: '150px'
        },
        {
            id: 'sourceId',
            field: 'sourceId.id',
            header: 'ASIN',
            width: '150px'
        },
        {
            id: 'title',
            field: 'title',
            header: 'Title',
            body: (rowData) => (
                <div
                    onClick={() => {
                        setAllowBookMain(false);
                        setProductDetail(rowData);
                    }}
                    className='hyperlink'
                    title={rowData?.title}
                >
                    {rowData?.title || 'Untitled'}
                </div>
            ),
            width: '300px'
        },
        {
            id: 'lastRefreshed',
            field: 'lastRefreshed.metadata',
            header: 'Last Refreshed',
            body: (rowData) => moment(rowData?.lastRefreshed?.metadata).format('DD MMM YYYY'), // format('DD MMM YYYY HH:mm:ss'),
            width: '180px'
        },
        {
            id: 'listPrice',
            field: 'pricing.listPrice.amount',
            header: 'List Price',
            body: (rowData) => rowData?.pricing?.listPrice
                ? `${Currency(rowData?.pricing?.listPrice?.currency)}${numeral(rowData?.pricing?.listPrice?.amount).format('0,0')}`
                : 'NA',
            width: '150px'
        },
        {
            id: 'publishedBy',
            field: 'book.publishedBy',
            header: 'Published By',
            body: (rowData) => rowData?.book?.publishedBy || 'NA',
            width: '200px'
        }
    ];

    return (
        <>
            <Toast ref={toast} position="top-right" />
            
            {allowBookMain ? (
                <>
                    <HeaderContainer>
                        <BookCountSection>
                            <i className="pi pi-book" />
                            <StyledLabelBlack className="book-count">
                                Books: {tableLoading ? '...' : totalNumberBooks}
                            </StyledLabelBlack>
                        </BookCountSection>

                        <SearchSection>
                            <TopFilter
                                searchKey={searchKey}
                                setSearchKey={setSearchKey}
                                searchFil={searchFil}
                                setSearchFil={setSearchFil}
                                setSearchFilter={setSearchFilter}
                                totalBooks={totalNumberBooks}
                                onRefresh={handleRefresh}
                            /> {/* Pass the refresh handler */}
                        </SearchSection>
                        {/*
                        <RefreshSection>
                            <ButtonRefresh
                                icon="-refresh"
                                tooltip="Refresh Data"
                                onClick={handleRefresh}
                                disabled={tableLoading}
                                className="refreshButton"
                            />
                        </RefreshSection>
                        */}
                    </HeaderContainer>

                    <TableContainer>
                        <BookTable
                            data={filterTableMainData.length > 0 && searchFil 
                                ? filterTableMainData 
                                : tableMainData}
                            columns={defaultColumns}
                            filters={filters}
                            setFilters={setFilters}
                            loading={tableLoading}
                        />
                    </TableContainer>

                    <Pagination
                        page={page}
                        setPage={setPage}
                        size={size}
                        setSize={setSize}
                        lastPage={lastPage}
                        sizeList={sizeList}
                        disabled={tableLoading}
                    />
                </>
            ) : (
                <ProductDetails
                    setAllowBookMain={setAllowBookMain}
                    productDetail={productDetail}
                    setProductDetail={setProductDetail}
                    tableData={tableData}
                    setTableData={setTableData}
                />
            )}

            {loading && (
                <LoadingOverlay>
                    <Loading />
                </LoadingOverlay>
            )}
        </>
    );
};

export default BookMainFile;